import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { toast } from "react-toastify"
import {
  getTeacherMe,
  getTeacherWorkItems,
  getTeacherAssignments,
  type TeacherMe,
  type TeacherWorkItem,
  type TeacherAssignmentItem,
} from "../../../service/teacher-portal"
import { all_routes } from "../../router/all_routes"

const routes = all_routes

const TeacherDashboard = () => {
  const [me, setMe] = useState<TeacherMe | null>(null)
  const [items, setItems] = useState<TeacherWorkItem[]>([])
  const [assignments, setAssignments] = useState<TeacherAssignmentItem[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([getTeacherMe(), getTeacherWorkItems(), getTeacherAssignments()])
      .then(([meRes, itemsRes, assignRes]) => {
        setMe(meRes.data)
        setItems(itemsRes.data?.items || [])
        setAssignments(assignRes.data?.items || [])
      })
      .catch(() => toast.error("Failed to load dashboard"))
      .finally(() => setLoading(false))
  }, [])

  const subjectItems = items.filter((i) => !i.is_final)
  const finalItems = items.filter((i) => i.is_final)
  const completeCount = subjectItems.filter((i) => i.complete).length
  const pendingCount = subjectItems.length - completeCount
  const classTeacherSections = assignments.filter((a) => a.is_class_teacher).length

  // Roll up to one row per class + campus
  const byClass = subjectItems.reduce<
    Record<
      string,
      {
        class_name: string
        campus_name: string
        sections: Set<string>
        subjects: Set<string>
        students: number
        items: number
        complete: number
      }
    >
  >((acc, it) => {
    const key = `${it.class_name}|${it.campus_name || ""}`
    if (!acc[key]) {
      acc[key] = {
        class_name: it.class_name,
        campus_name: it.campus_name || "",
        sections: new Set(),
        subjects: new Set(),
        students: 0,
        items: 0,
        complete: 0,
      }
    }
    if (it.section_name) acc[key].sections.add(it.section_name)
    if (it.subject_name) acc[key].subjects.add(it.subject_name)
    acc[key].students += Number(it.student_count) || 0
    acc[key].items += 1
    if (it.complete) acc[key].complete += 1
    return acc
  }, {})
  const classRows = Object.values(byClass)

  const firstName = me?.user?.firstName || "Teacher"
  const session = me?.active_session

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="mb-4">
          <h3 className="page-title mb-1">Welcome, {firstName}</h3>
          <p className="text-muted mb-0">
            {session
              ? `Active session: ${session.title || session.name}`
              : "No active session — contact admin."}
          </p>
        </div>

        {/* Stats */}
        <div className="row g-3 mb-4">
          <StatCard
            label="Assignments"
            value={assignments.length}
            hint="class · section · subject"
          />
          <StatCard
            label="Pending"
            value={pendingCount}
            hint={subjectItems.length ? `of ${subjectItems.length} work items` : "no items"}
            tone={pendingCount > 0 ? "warning" : "secondary"}
          />
          <StatCard
            label="Complete"
            value={completeCount}
            hint={subjectItems.length ? `of ${subjectItems.length} work items` : "—"}
            tone="success"
          />
          <StatCard
            label="Class teacher of"
            value={classTeacherSections}
            hint={classTeacherSections === 1 ? "section" : "sections"}
            tone={classTeacherSections > 0 ? "warning" : "secondary"}
          />
        </div>

        {/* Quick links */}
        <div className="row g-3 mb-4">
          <div className="col-md-6">
            <Link
              to={routes.marks}
              className="card border text-decoration-none hover-shadow d-block"
            >
              <div className="card-body d-flex align-items-center justify-content-between">
                <div>
                  <div className="fw-semibold">Mark Entry</div>
                  <small className="text-muted">Enter / submit subject marks</small>
                </div>
                <i className="ti ti-arrow-right text-primary fs-4" />
              </div>
            </Link>
          </div>
          <div className="col-md-6">
            <Link
              to={routes.teacherFinalTerm}
              className={`card border text-decoration-none d-block ${
                finalItems.length === 0 ? "opacity-50 pe-none" : "hover-shadow"
              }`}
            >
              <div className="card-body d-flex align-items-center justify-content-between">
                <div>
                  <div className="fw-semibold">Final Term Carryover</div>
                  <small className="text-muted">
                    {finalItems.length > 0
                      ? `${finalItems.length} pending`
                      : "No final-term tasks"}
                  </small>
                </div>
                <i className="ti ti-arrow-right text-primary fs-4" />
              </div>
            </Link>
          </div>
        </div>

        {/* Progress by class */}
        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between">
            <h5 className="card-title mb-0">Progress by class</h5>
            <Link to={routes.marks} className="text-primary small">
              Go to Mark entry →
            </Link>
          </div>
          <div className="card-body p-0">
            {loading ? (
              <p className="text-muted p-3">Loading…</p>
            ) : classRows.length === 0 ? (
              <div className="text-center text-muted py-5">
                <p>No assignments yet.</p>
                <small>
                  Once admin assigns you to class·section·subject combinations and creates course
                  structures, your classes will appear here.
                </small>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Class</th>
                      <th>Campus</th>
                      <th>Sections</th>
                      <th>Subjects</th>
                      <th>Students</th>
                      <th>Progress</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {classRows.map((c) => {
                      const pct = c.items ? Math.round((c.complete / c.items) * 100) : 0
                      return (
                        <tr key={`${c.class_name}-${c.campus_name}`}>
                          <td className="fw-semibold">{c.class_name}</td>
                          <td className="text-muted">{c.campus_name || "—"}</td>
                          <td>{c.sections.size || 1}</td>
                          <td>{c.subjects.size}</td>
                          <td>{c.students}</td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <div
                                className="progress flex-grow-1"
                                style={{ height: 6, width: 120 }}
                              >
                                <div
                                  className={`progress-bar ${pct === 100 ? "bg-success" : "bg-primary"}`}
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                              <small className="text-muted">
                                {c.complete}/{c.items} ({pct}%)
                              </small>
                            </div>
                          </td>
                          <td>
                            <Link to={routes.marks} className="text-primary small">
                              Go to Mark entry →
                            </Link>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const StatCard = ({
  label,
  value,
  hint,
  tone = "secondary",
}: {
  label: string
  value: number
  hint?: string
  tone?: "success" | "warning" | "secondary"
}) => {
  const bg =
    tone === "success"
      ? "bg-success-transparent"
      : tone === "warning"
      ? "bg-warning-transparent"
      : "bg-secondary-transparent"
  return (
    <div className="col-6 col-md-3">
      <div className={`card ${bg} border-0`}>
        <div className="card-body py-3">
          <div className="small text-uppercase fw-semibold text-muted mb-1">{label}</div>
          <div className="fs-3 fw-bold">{value}</div>
          {hint && <div className="small text-muted">{hint}</div>}
        </div>
      </div>
    </div>
  )
}

export default TeacherDashboard
